import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import React, { cloneElement, useCallback } from 'react';
import AbstractSwitch from '../../../abstract/AbstractSwitch';
import { useRadioButtonGroupContext } from '../../../molecules/RadioButtonGroup/useRadioButtonGroup';
import { useDefaultComponentValue } from '../../../utils/useDefaultComponentValue';
import RadioSvg from '../../SvgIcon/Icons/Radio';
import RadioCheckedSvg from '../../SvgIcon/Icons/RadioChecked';
const COMPONENT_NAME = 'RadioButton';
const RadioButton = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { label, labelClassName, disabled, checked: checkedProp, checkedIcon = /*#__PURE__*/ React.createElement(RadioCheckedSvg, null), uncheckedIcon = /*#__PURE__*/ React.createElement(RadioSvg, null), disabledCheckedIcon, disabledUncheckedIcon, value, tabIndex = 0, className, onCheck, ...other } = props;
    let checked = checkedProp;
    const radioButtonGroup = useRadioButtonGroupContext();
    if (radioButtonGroup) {
        checked = checked ?? value === radioButtonGroup.value;
    }
    const radioGroupOnChange = radioButtonGroup?.onChange;
    const handleChange = useCallback((e)=>{
        onCheck?.(e, value);
        radioGroupOnChange?.(e, value);
    }, [
        onCheck,
        radioGroupOnChange,
        value
    ]);
    const handleKeyPressForAbstractSwitch = useCallback((e)=>{
        e.preventDefault();
        if (e.charCode === 32) {
            handleChange(e);
        }
    }, [
        handleChange
    ]);
    const icon = checked ? disabled && disabledCheckedIcon || checkedIcon : disabled && disabledUncheckedIcon || uncheckedIcon;
    return /*#__PURE__*/ React.createElement("div", {
        className: cx('Radio', {
            'Radio--checked': checked
        }, {
            'Radio--disabled': disabled
        }, className)
    }, /*#__PURE__*/ React.createElement("label", {
        className: cx('Radio-label', labelClassName)
    }, /*#__PURE__*/ React.createElement("span", {
        tabIndex: disabled ? undefined : tabIndex,
        className: "Radio-box",
        onKeyPress: handleKeyPressForAbstractSwitch
    }, /*#__PURE__*/ cloneElement(icon, {
        className: 'Radio-icon'
    }), /*#__PURE__*/ React.createElement(AbstractSwitch, {
        className: cx('Radio-control'),
        inputType: "radio",
        switched: checked,
        onSwitch: handleChange,
        disabled: disabled,
        value: value,
        ...other
    })), /*#__PURE__*/ React.createElement("span", {
        className: "Radio-text"
    }, label)));
};
RadioButton.propTypes = {
    /**
	 * If true, the radion button field will be selected.
	 */ checked: PropTypes.bool,
    /**
	 * Icon for checked state
	 */ checkedIcon: PropTypes.node,
    /**
	 * Root className.
	 */ className: PropTypes.string,
    /**
	 * If true, the radio button field will be disabled.
	 */ disabled: PropTypes.bool,
    /**
	 * Disabled checked state icon node
	 */ disabledCheckedIcon: PropTypes.node,
    /**
	 * Disabled unchecked state icon node
	 */ disabledUncheckedIcon: PropTypes.node,
    /**
	 * Text displayed next to radio button.
	 */ label: PropTypes.node,
    /**
	 * ClassName applied to `label` element.
	 */ labelClassName: PropTypes.string,
    /**
	 * Name of the input field.
	 */ name: PropTypes.string,
    /**
	 * Callback function fired when radion button is selected.
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothin'
	 */ onCheck: PropTypes.func,
    /**
	 * tabindex html property of wrapping element
	 */ tabIndex: PropTypes.number,
    /**
	 * Icon for unchecked state
	 */ uncheckedIcon: PropTypes.node,
    /**
	 * Value of the input field.
	 */ value: PropTypes.any
};
export default RadioButton;
